import * as queryString from 'query-string'
import { ArrayUtils } from '@shopping/common'

import history from 'common/history'
import { isBrowser } from 'common/utils'
import { isEmptyObject } from 'common/utils/logic/object'
import {
  getSubVerticalPath,
  getVerticalPath,
  isReactHome,
  SubVertical,
  usedReactHome,
  Vertical,
} from 'common/constants'
import hostConfig from 'common/config'
import { Content } from 'common/types/entities/mongo/content'
import { isMobileEnv } from 'common/utils/common'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import { SpecialEvent } from 'common/types/entities/api/specialEvent'
import projectConfig from 'common/config/project'
import { verticalConfig } from 'common/constants/verticalService'

type Params = { [key: string]: any }

export const replaceUrl = (params?: Params, hash?: string) => {
  if (!isBrowser) return
  const queryParams = queryString.parse(window.location.search)
  if (params && !isEmptyObject(params)) {
    Object.keys(params).forEach(key => {
      queryParams[key] = params[key] ? params[key] : undefined
    })
  }
  const search = queryString.stringify(queryParams)
  let url = `${window.location.pathname}?${search}`
  if (hash) {
    url = `${url}#${hash}`
  }
  history.replace(url)
}

export const getBranchUrl = (
  branchId: string,
  subVertical: SubVertical,
  isMobile: boolean,
  includeHost: boolean = true,
) => {
  const { pc, mobile } = hostConfig.shoppingWebUri
  const config = isMobile ? mobile : pc
  return `${includeHost ? config.host : ''}${getSubVerticalPath(subVertical)}/branch/${branchId}`
}

export function getContentUrl(content: Content, isMobile: boolean, includeHost: boolean = true) {
  const { _id } = content

  if (content.branch && content.branch.subVertical && ArrayUtils.isNotEmpty(content.branch.storeCategory)) {
    const { subVertical, storeCategory } = content.branch
    const branchId = storeCategory[storeCategory.length - 1]

    return `${getBranchUrl(branchId, subVertical, isMobile, includeHost)}/news/${_id}`
  }

  const channeNo = content.channel._id
  const subVertical = content.channel.storeCategoryRelations[0].subVertical

  return `${getStoreUrl(channeNo, subVertical, isMobile, includeHost)}/news/${_id}`
}

export const getStoreUrl = (
  storeId: string,
  subVertical: SubVertical,
  isMobile: boolean,
  includeHost: boolean = true,
) => {
  const { pc, mobile } = hostConfig.shoppingWebUri
  const config = isMobile ? mobile : pc
  return `${includeHost ? config.host : ''}${getSubVerticalPath(subVertical)}/stores/${storeId}`
}

export const getHomeUrl = (includeHost: boolean = true) => {
  const { pc, mobile } = hostConfig.verticalUri

  if (isMobileEnv) {
    return `${includeHost ? mobile : ''}${getVerticalPath(Vertical.HOME)}/m/index.nhn`
  }

  return `${includeHost ? pc : ''}${getVerticalPath(Vertical.HOME)}/p/index.nhn`
}

export function getRelationProductUrl(
  relationProduct: RelationProduct,
  isMobile: boolean,
  includeHost: boolean = true,
) {
  const { relationProductNo, channel } = relationProduct

  const channeNo = channel._id
  const subVertical = channel.storeCategoryRelations[0].subVertical

  return `${getStoreUrl(channeNo, subVertical, isMobile, includeHost)}/relation-products/${relationProductNo}`
}

export function getSpecialEventUrl(event: SpecialEvent) {
  const { vertical, subVertical } = event

  let url
  if (subVertical) {
    url = getSubVerticalPath(subVertical)
  } else if (vertical) {
    url = getVerticalPath(vertical)
  }

  return `${url}/event/${event.id}`
}

export const getShoppingWebUrl = (env?: 'mobile' | 'pc'): string => {
  const shoppingWeb = hostConfig.shoppingWebUri[env || process.env.PROJECT_NAME]
  return `${shoppingWeb.protocol}://${shoppingWeb.host}`
}

export const getVerticalHomeUrl = (vertical: Vertical) => {
  if (verticalConfig[vertical] && !verticalConfig[vertical].isWindow) {
    return `${getShoppingWebUrl()}${verticalConfig[vertical].homeUrl[process.env.PROJECT_NAME]}`
  }

  if (isReactHome(vertical)) {
    return `${getShoppingWebUrl()}${getVerticalPath(vertical)}/home`
  }

  const windowUri = hostConfig.windowUri[projectConfig.project]
  return `${windowUri}${getVerticalPath(vertical)}/home`
}

export const getWindowHomeUrl = (subVertical: SubVertical) => {
  if (usedReactHome(subVertical)) {
    if (isBrowser) {
      return `${getSubVerticalPath(subVertical)}/home`
    }
    return `${getShoppingWebUrl()}${getSubVerticalPath(subVertical)}/home`
  }

  const windowUri = hostConfig.windowUri[projectConfig.project]
  return `${windowUri}${getSubVerticalPath(subVertical)}/home`
}

export const getCoordiUrl = (subVertical: SubVertical, menuId: string): string => {
  return `${getSubVerticalPath(subVertical)}/coordi/${menuId}`
}

export const hasDomain = url => /^(https:\/\/|http:\/\/)?(.*\.)?naver.com/.test(url)
